import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { getBrowserTimezone, getTimezoneOptions } from '../lib/timezones';
import { useNavigate } from 'react-router-dom';
import { setPageMeta } from '../lib/seo';

const Register: React.FC = () => {
  const { register, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    timezone: getBrowserTimezone(),
  });
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  const timezoneOptions = getTimezoneOptions();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    setPageMeta({
      title: 'Create Account — Elorimi',
      description: 'Create your Elorimi account to access Cloud PC and Cloud VPS resources.',
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedPolicies) {
      notification.error({ message: 'Please accept the Terms and Privacy Policy' });
      return;
    }
    setLoading(true);
    try {
      await register(formData.firstName, formData.lastName, formData.email, formData.password, formData.timezone);
      notification.success({ message: 'Account created successfully' });
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error || 'Failed to register' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card glass">
          <h2 className="auth-title">Create Account</h2>
          <p className="auth-subtitle">Join our platform today</p>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                placeholder="First Name"
                required
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Last Name"
                required
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
            <input
              type="email"
              placeholder="Email"
              required
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            />
            <input
              type="password"
              placeholder="Password"
              required
              value={formData.password}
              onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
            />
            <select
              value={formData.timezone}
              onChange={(e) => setFormData((prev) => ({ ...prev, timezone: e.target.value }))}
              required
            >
              {timezoneOptions.map((tz: string) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <label className="auth-consent">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
              />
              <span>
                By registering you agree to our{' '}
                <a href="/terms">Terms of Service</a> and{' '}
                <a href="/privacy">Privacy Policy</a>.
              </span>
            </label>
            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>
          <p className="auth-switch">
            Already have an account? <a href="/login">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
